import { useNavigate } from "react-router-dom"
import { CheckSquare, Users, Calendar, UserPlus, Loader2 } from "lucide-react"

interface SearchResults {
  contacts: any[]
  tasks: any[]
  events: any[]
  leads: any[]
}

interface GlobalSearchResultsProps {
  results: SearchResults
  isSearching: boolean
  query: string
  onClose: () => void
}

interface RenderItemResult {
  title: string
  detail: string
  detailColor?: string
}

const SECTIONS: Array<{
  key: "tasks" | "contacts" | "events" | "leads"
  label: string
  icon: typeof CheckSquare
  route: string
  renderItem: (item: any) => RenderItemResult
}> = [
  {
    key: "tasks" as const,
    label: "Task",
    icon: CheckSquare,
    route: "/tasks",
    renderItem: (item: any) => ({
      title: item.title || "Senza titolo",
      // FE-7: mostra il progetto se presente, altrimenti lo status
      // Se il task è inbox (project === null), mostra lo status o "Inbox"
      detail: item.project_detail?.name || item.status?.name || "Inbox",
      // Usa il colore del progetto se presente, altrimenti quello dello status
      detailColor: item.project_detail?.color || item.status?.color,
    }),
  },
  {
    key: "contacts" as const,
    label: "Contatti",
    icon: Users,
    route: "/contacts",
    renderItem: (item: any) => {
      const name =
        item.contact_type === "company"
          ? item.company_name || "Azienda"
          : `${item.first_name || ""} ${item.last_name || ""}`.trim() || "Contatto"
      return {
        title: name,
        detail: item.email || item.mobile_phone || "",
      }
    },
  },
  {
    key: "events" as const,
    label: "Appuntamenti",
    icon: Calendar,
    route: "/agenda",
    renderItem: (item: any) => ({
      title: item.title || "Senza titolo",
      detail: item.date
        ? new Date(item.date).toLocaleDateString("it-IT", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "",
    }),
  },
  {
    key: "leads" as const,
    label: "Leads",
    icon: UserPlus,
    route: "/leads",
    renderItem: (item: any) => ({
      title: item.full_name || item.email || "Lead",
      detail: item.company || item.email || "",
    }),
  },
]

export function GlobalSearchResults({
  results,
  isSearching,
  query,
  onClose,
}: GlobalSearchResultsProps) {
  const navigate = useNavigate()

  const totalResults =
    results.contacts.length +
    results.tasks.length +
    results.events.length +
    results.leads.length

  const handleItemClick = (route: string, itemId: number | string) => {
    navigate(route, { state: { openDetailId: itemId } })
    onClose()
  }

  return (
    <div
      className="absolute top-full left-0 mt-2 rounded-xl shadow-lg overflow-hidden z-50 scrollbar-hexa"
      style={{
        width: "420px",
        maxHeight: "400px",
        overflowY: "auto",
        backgroundColor: "var(--hexa-surface)",
        border: "1px solid var(--hexa-border)",
      }}
    >
      {isSearching ? (
        <div
          className="flex items-center justify-center gap-2 py-8"
          style={{ color: "var(--hexa-text-secondary)" }}
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Ricerca in corso...</span>
        </div>
      ) : totalResults === 0 ? (
        <div
          className="py-8 text-center text-sm"
          style={{ color: "var(--hexa-text-secondary)" }}
        >
          Nessun risultato per "{query}"
        </div>
      ) : (
        SECTIONS.map((section, sectionIdx) => {
          const items = results[section.key]
          if (!items || items.length === 0) return null

          return (
            <div key={section.key}>
              {sectionIdx > 0 && (
                <div style={{ borderBottom: "1px solid var(--hexa-border)" }} />
              )}
              {/* Section header */}
              <div
                className="flex items-center gap-2 px-4 py-2"
                style={{ color: "var(--hexa-text-muted)" }}
              >
                <section.icon className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold uppercase tracking-wider">
                  {section.label}
                </span>
                <span className="text-xs">({items.length})</span>
              </div>

              {/* Items */}
              {items.map((item: any, idx: number) => {
                const { title, detail, detailColor } = section.renderItem(item)
                return (
                  <div
                    key={item.id || idx}
                    className="flex items-center justify-between px-4 py-2 cursor-pointer transition-colors"
                    style={{ color: "var(--hexa-text-primary)" }}
                    onClick={() => handleItemClick(section.route, item.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        "var(--hexa-surface-hover)"
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent"
                    }}
                  >
                    <span className="text-sm font-medium truncate mr-3">
                      {title}
                    </span>
                    {detail && (
                      <span
                        className="text-xs shrink-0 truncate max-w-[160px]"
                        style={{
                          color: detailColor || "var(--hexa-text-secondary)",
                        }}
                      >
                        {detail}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })
      )}
    </div>
  )
}
